import {
  CREDIT_METRICS,
  GROUP_STAT_LABEL,
  INCOME_METRICS,
  METRICS_WITH_EMP,
  METRIC_PLACEHOLDER_VALUES
} from "../../constants";
import { cx, toMoney } from "shared/utils";
import {
  collapseRoles,
  formatValueByType,
  getRelevantApplicantId
} from "./utils";
import { MetricBadge } from "./MetricBadge";
import { ApplicationMetricData, GroupStatMetricNames } from "api/applications";
import { Hide, Show } from "shared/components";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { useMemo } from "react";
import {
  AlertCircleIcon,
  CalculatorIcon,
  Delete01Icon,
  Edit02Icon,
  ViewIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { useConfigurableGroupMetric } from "../../hooks/use-configurable-group-metric";
import { track } from "shared/telemetry/track";
import { useAuthContext } from "providers/Auth";
import { PermittedField } from "./PermittedField";
import { useNavigate, useParams } from "react-router-dom";

const DEFAULT_NEW_WIDGET_TYPE = GroupStatMetricNames.GrossIncome;

type MetricWidgetProps =
  | { isPreview: true; metricData: Partial<ApplicationMetricData> }
  | {
      isPreview?: false;
      metricData: ApplicationMetricData;
      editMetric: (m: ApplicationMetricData) => void;
      setShowEditEmpModal: (v: boolean) => void;
      setShowMetricBreakdownModal: (v: ApplicationMetricData | null) => void;
    };

// Props not destructured so `isPreview` can discriminate partial vs full ApplicationMetricData
export const MetricWidget = (props: MetricWidgetProps) => {
  const { permissions } = useAuthContext();
  const navigate = useNavigate();
  const { applicationId } = useParams();

  const { metricData, isPreview = false } = props;
  const metricType = metricData?.metric || DEFAULT_NEW_WIDGET_TYPE;

  const hasPermissionsToView = useMemo(() => {
    if (isPreview) return true;
    if (CREDIT_METRICS.includes(metricType))
      return permissions.canAccessReports.creditReport;
    if (INCOME_METRICS.includes(metricType))
      return permissions.canAccessReports.incomeReport;
    return true;
  }, [isPreview, metricData, permissions]);

  const { removeMetricByIndex } = useConfigurableGroupMetric();

  const contextPopoverActions = useMemo(() => {
    if (props.isPreview) return [];
    const {
      metricData,
      editMetric,
      setShowEditEmpModal,
      setShowMetricBreakdownModal
    } = props;

    const actions = [
      {
        title: "Remove metric",
        icon: Delete01Icon,
        onClick: () => removeMetricByIndex(metricData.index)
      },
      {
        title: "Configure metric widget",
        icon: Edit02Icon,
        onClick: () => editMetric(metricData)
      }
    ];
    if (!hasPermissionsToView) return actions.reverse();

    if (METRICS_WITH_EMP.includes(metricType) && setShowEditEmpModal)
      actions.push({
        title: "Edit EMP",
        icon: Edit02Icon,
        onClick: () => {
          track("summary_metrics.emp_edit.page_view", {
            application_id: String(applicationId)
          });
          setShowEditEmpModal(true);
        }
      });

    if (Boolean(metricData.applicants.length))
      actions.push({
        title: "View calculation",
        icon: CalculatorIcon,
        onClick: () => {
          track("summary_metrics.calculation.page_view", {
            application_id: String(applicationId),
            metric_name: metricType
          });
          setShowMetricBreakdownModal(metricData);
        }
      });

    const oneApplicantWithoutCredit =
      metricType === GroupStatMetricNames.NoCreditScorePeople &&
      metricData.applicants.length === 1;
    const shouldLinkCreditReport =
      oneApplicantWithoutCredit ||
      [
        GroupStatMetricNames.MinCreditScore,
        GroupStatMetricNames.MaxCreditScore
      ].includes(metricType);

    if (shouldLinkCreditReport) {
      const applicantId = getRelevantApplicantId(metricData);
      if (applicantId)
        actions.push({
          title: `View credit report`,
          icon: ViewIcon,
          onClick: () => {
            track("summary_metrics.report.selected", {
              application_id: String(applicationId)
            });
            navigate(`reports?applicantId=${applicantId}&report=credit_report`);
          }
        });
    }
    return actions.reverse();
  }, [metricData, metricType, hasPermissionsToView]);

  const { formattedMetric, formattedEMP } = useMemo(() => {
    if (isPreview)
      return {
        formattedMetric: METRIC_PLACEHOLDER_VALUES[metricType],
        formattedEMP: "$6,100"
      };
    if (!metricData)
      return {
        formattedMetric: "-",
        formattedEMP: "-"
      };
    return {
      formattedMetric: formatValueByType(metricData.value, metricData.metric),
      formattedEMP: toMoney(metricData.debt || 0)
    };
  }, [isPreview, metricData]);

  const shouldShowApplicationRoles = metricData?.role_label;
  const hasWarnings = Boolean(metricData.warnings?.length);
  const metricCategory = INCOME_METRICS.includes(metricType)
    ? "income"
    : "credit";

  return (
    <div
      className={cx(
        "border border-alpha-100 rounded-xl p-6 max-w-xl",
        isPreview ? "" : "m-1.5"
      )}
    >
      <div className="flex items-center justify-between">
        <p className="text-content-1000 text-sm font-medium">
          {metricData?.title || GROUP_STAT_LABEL[metricType]}
          <Show if={hasPermissionsToView && hasWarnings}>
            <Tooltip placement="bottom" delay={{ close: 150 }}>
              <TooltipTrigger>
                <HugeiconsIcon
                  icon={AlertCircleIcon}
                  size={18}
                  strokeWidth={1.5}
                  className="text-warning-600 inline ml-1.5 mb-1"
                />
              </TooltipTrigger>
              <TooltipContent className="rounded-lg min-w-80 border bg-alpha-800 p-4 text-sm text-content-1000-inverted z-50">
                <p className="mb-3 text-sm">
                  Some {metricCategory} reports have warnings
                </p>
                {(metricData.warnings || []).map(({ id, name }) => (
                  <div
                    className="text-xs flex justify-between mt-2"
                    key={`warning-${id}`}
                  >
                    <p>{name}</p>
                    <span
                      className="font-bold cursor-pointer"
                      onClick={() => {
                        track("summary_metrics.report.selected", {
                          application_id: String(applicationId)
                        });
                        navigate(
                          `reports?applicantId=${id}&report=${metricCategory === "credit" ? "credit_report" : "combined_income_verification"}`
                        );
                      }}
                    >
                      View report
                    </span>
                  </div>
                ))}
              </TooltipContent>
            </Tooltip>
          </Show>
        </p>
        <Hide if={isPreview}>
          <div className="size-5">
            <ContextMenuPopover
              placement="bottom-start"
              actions={contextPopoverActions.map((action) => ({
                title: (
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon
                      icon={action.icon}
                      size={20}
                      className="text-content-200"
                    />
                    {action.title}
                  </div>
                ),
                onClick: action.onClick
              }))}
              triggerClassName="rotate-90 -translate-x-1/4 -translate-y-1/4"
              contentClassName="z-[500] shadow-xl"
            />
          </div>
        </Hide>
      </div>

      <div className="my-2">
        <PermittedField isPermitted={hasPermissionsToView || isPreview}>
          <div className="flex items-center gap-2">
            <span className="text-content-1000 text-2xl font-bold">
              {formattedMetric}
            </span>

            <Show if={METRICS_WITH_EMP.includes(metricType)}>
              <Tooltip placement="bottom">
                <TooltipTrigger>
                  <span className="text-content-400 font-medium text-xs">
                    EMP {formattedEMP}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="rounded-lg z-[150] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                  Estimated monthly payments
                </TooltipContent>
              </Tooltip>
            </Show>

            <Show if={metricData?.badge}>
              <MetricBadge metricType={metricType} />
            </Show>
          </div>
        </PermittedField>
      </div>

      <Show if={shouldShowApplicationRoles}>
        <p className="text-content-400 text-xs">
          {collapseRoles(metricData?.roles)}
        </p>
      </Show>
    </div>
  );
};
